import { DataButton, Icon } from "@sellar-io/components";
import { Transition } from "@headlessui/react";
import { useRef, useState, type ReactNode } from "react";

const SWIPE_DISTANCE = 40;

export function HomeNotificationStack({
  orders,
  loadMore,
}: {
  orders: { id: number; content: ReactNode }[];
  loadMore?: ReactNode;
}) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showFront, setShowFront] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const pendingId = useRef<number | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const selectedIndex = Math.max(
    0,
    orders.findIndex((order) => order.id === selectedId),
  );
  const selected = orders[selectedIndex];
  const select = (id: number) => {
    if (transitioning || id === selected?.id) {
      return;
    }
    pendingId.current = id;
    setTransitioning(true);
    setShowFront(false);
  };
  const move = (direction: number) => {
    const next =
      orders[(selectedIndex + direction + orders.length) % orders.length];
    if (next) {
      select(next.id);
    }
  };

  if (!selected) {
    return null;
  }

  return (
    <section
      aria-label="Orders requiring attention"
      aria-roledescription="carousel"
      className="flex items-center gap-3"
    >
      <div
        className="relative min-w-0 flex-1 touch-pan-x pt-3"
        onTouchStart={(event) => {
          const touch = event.touches[0];
          touchStart.current = touch
            ? { x: touch.clientX, y: touch.clientY }
            : null;
        }}
        onTouchCancel={() => {
          touchStart.current = null;
        }}
        onTouchEnd={(event) => {
          const start = touchStart.current;
          touchStart.current = null;
          if (!start || orders.length < 2) {
            return;
          }
          const touch = event.changedTouches[0];
          if (!touch) {
            return;
          }
          const dx = touch.clientX - start.x;
          const dy = touch.clientY - start.y;
          if (Math.abs(dy) >= SWIPE_DISTANCE && Math.abs(dy) > Math.abs(dx)) {
            move(dy < 0 ? 1 : -1);
          }
        }}
      >
        {orders.length > 2 && (
          <div
            aria-hidden="true"
            className="absolute inset-x-4 top-0 bottom-3 rounded-2xl border border-zinc-200 bg-white opacity-25 shadow-[1px_1px_16px_0_rgba(0,0,0,0.05)]"
          />
        )}
        {orders.length > 1 && (
          <div
            aria-hidden="true"
            className="absolute inset-x-2 top-1.5 bottom-1.5 rounded-2xl border border-zinc-200 bg-white opacity-50 shadow-[1px_1px_16px_0_rgba(0,0,0,0.05)]"
          />
        )}
        <Transition
          show={showFront}
          enter="transition duration-250 ease-out motion-reduce:duration-0"
          enterFrom="-translate-y-1.5 scale-[0.986] opacity-50"
          enterTo="translate-y-0 scale-100 opacity-100"
          leave="pointer-events-none transition duration-150 ease-in motion-reduce:duration-0"
          leaveFrom="translate-y-0 scale-100 opacity-100"
          leaveTo="-translate-y-3 scale-[0.97] opacity-0"
          afterLeave={() => {
            setSelectedId(pendingId.current);
            setShowFront(true);
          }}
          afterEnter={() => setTransitioning(false)}
        >
          <div
            className="relative rounded-2xl border border-zinc-200 bg-white p-4 shadow-[1px_1px_16px_0_rgba(0,0,0,0.05)]"
            aria-roledescription="slide"
            aria-label={`${selectedIndex + 1} of ${orders.length}`}
            aria-busy={transitioning}
          >
            <div key={selected.id}>{selected.content}</div>
          </div>
        </Transition>
      </div>
      {(orders.length > 1 || loadMore) && (
        <div className="flex shrink-0 flex-col items-center gap-2">
          <DataButton
            border
            disabled={transitioning}
            aria-label="Previous notification"
            onClick={() => move(-1)}
            className="hidden h-8 w-8 p-2 text-zinc-800 md:flex"
          >
            <Icon icon="CHEVRON_UP" size="16px" />
          </DataButton>
          <div className="flex max-h-32 flex-col overflow-y-auto">
            {orders.map((order, index) => (
              <button
                key={order.id}
                type="button"
                aria-label={`Show notification ${index + 1}`}
                aria-current={index === selectedIndex ? "true" : undefined}
                disabled={transitioning}
                onClick={() => select(order.id)}
                className="flex h-4 w-4 cursor-pointer items-center justify-center disabled:cursor-not-allowed"
              >
                <span
                  className={`h-2 w-2 rounded-full ${index === selectedIndex ? "bg-zinc-600" : "bg-zinc-200"}`}
                />
              </button>
            ))}
          </div>
          <DataButton
            border
            disabled={transitioning}
            aria-label="Next notification"
            onClick={() => move(1)}
            className="hidden h-8 w-8 p-2 text-zinc-800 md:flex"
          >
            <Icon icon="CHEVRON_DOWN" size="16px" />
          </DataButton>
          {loadMore}
        </div>
      )}
    </section>
  );
}
